import omit from 'lodash/omit';
import isEmpty from 'lodash/isEmpty';
import type { State } from '../types';
import { isNewEntitlement } from '.';
import { CartProductSchema, OperationType } from '../../types';

export function getProductIdsWithoutChanges({
  selectedProducts,
  selectedLinkedEntitlements,
  products,
  linkedEntitlements,
  nextBillDate,
}: {
  selectedProducts: State['selectedProducts'];
  selectedLinkedEntitlements?: State['selectedLinkedEntitlements'];
  products: State['products'];
  linkedEntitlements: State['linkedEntitlements'];
  nextBillDate: State['nextBillDate'];
}): string[] {
  const productIdsWithoutChanges = Object.keys(selectedProducts)?.filter((entitlementId) => {
    const productDetails = products[entitlementId];
    const isNew = isNewEntitlement(productDetails);
    const isTransfer = productDetails.operationType === OperationType.TRANSFER;
    // If in product details, parentEntitlementId is present, then it is an org instance.
    const isOrgInstance = !!productDetails.parentEntitlementId;

    // Org instances are read only, and modified based on parent entitlement.
    if (isNew || isTransfer || isOrgInstance) return false;

    const hasNewOrChangedApps = doesProductHaveNewOrChangedLinkedApps({
      entitlementId,
      products,
      linkedEntitlements,
      selectedLinkedEntitlements,
    });

    /**
     user is adding a new app to an existing entitlement or has made some change to an existing app (currently only with promocode, but the not empty check done below will scale to other changes if any).

     We should allow the parent entitlement to be flagged as changed as only then the user would be allowed to move forward to the order summary screen.
     In the summary screen before placing order, we will filter out the unchanged product.
     */
    if (hasNewOrChangedApps) return false;

    const changes = selectedProducts[entitlementId];
    // Now next bill date will be added to changed products hash. We are still checking on the global store level change because it is not handled for all usecases: product addition after setting cloudsite level bill date.
    const changesWithoutNextBillDate = omit(changes, ['productStatuses', 'nextBillDate']);
    const { id, cloudSite } = productDetails;
    const relatedBillDateChange =
      nextBillDate.cartLevel || nextBillDate.cloudSiteLevel?.[cloudSite] || nextBillDate.entitlementLevel?.[id];

    const isChangeSetEmpty = isEmpty(changesWithoutNextBillDate);

    return isChangeSetEmpty && !relatedBillDateChange;
  });

  return productIdsWithoutChanges;
}

function doesProductHaveNewOrChangedLinkedApps({
  entitlementId,
  products,
  linkedEntitlements,
  selectedLinkedEntitlements,
}: {
  entitlementId: CartProductSchema['id'];
  products: State['products'];
  linkedEntitlements: State['linkedEntitlements'];
  selectedLinkedEntitlements: State['selectedLinkedEntitlements'];
}) {
  const product = products[entitlementId];
  const containerOrProductId = product.containerId || entitlementId;
  const relatedApps = linkedEntitlements[containerOrProductId] || [];
  const newApps = relatedApps?.filter((app) => app?.operationType === OperationType.NEW);

  const hasNewApps = newApps.length > 0;
  const relatedAppWithChange = relatedApps?.find((app) => {
    const appChanges = omit(selectedLinkedEntitlements?.[app?.id], 'productStatuses');
    return !isEmpty(appChanges);
  });
  const hasRelatedAppWithChange = !!relatedAppWithChange;

  return hasNewApps || hasRelatedAppWithChange;
}
